import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { Button } from "@/src/components/ui/button";
import { fetch } from "@/src/lib/fetch";
import { useToast } from "@/src/components/ui/use-toast";

/**
 * Other documents on the HubSpot deal (brief §6c). Read live from HubSpot each
 * load, so files added in HubSpot show straight away; upload writes to HubSpot
 * and attaches to the deal; remove detaches (never deletes the file).
 */

type DealFile = {
  fileId: string;
  noteId: string;
  name: string;
  extension: string | null;
  size: number | null;
  createdAt: string | null;
  note: string | null;
  fromHelm: boolean;
};

const fmtSize = (n: number | null) => (n == null ? "" : n > 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`);

export function DocumentsSection({ bookingId, hasDeal }: { bookingId: string; hasDeal: boolean }) {
  const { toast } = useToast();
  const [state, setState] = useState<{ live: boolean; files: DealFile[]; error?: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    if (!hasDeal) return;
    fetch<{ data: { live: boolean; files: DealFile[]; error?: string } }>(`/api/bookings/${bookingId}/hubspot-files`)
      .then((r: any) => setState(r.data))
      .catch((e: any) => setState({ live: false, files: [], error: e?.message }));
  }, [bookingId, hasDeal]);
  useEffect(load, [load]);

  const upload = async (file: File) => {
    setBusy("upload");
    try {
      const form = new FormData();
      form.append("file", file);
      await fetch(`/api/bookings/${bookingId}/hubspot-files`, "POST", form);
      toast({ title: "Uploaded to HubSpot", description: `${file.name} is attached to the deal.` });
      load();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e?.message ?? "HubSpot rejected the file.", variant: "destructive" });
    } finally {
      setBusy(null);
      if (input.current) input.current.value = "";
    }
  };

  // A plain link into the API's redirect: opened from the click itself, so never popup-blocked.
  const openHref = (f: DealFile) => `/api/bookings/${bookingId}/hubspot-files/${f.fileId}/open`;

  const remove = async (f: DealFile) => {
    if (!window.confirm(`Detach "${f.name}" from this deal? The file stays in HubSpot's file manager.`)) return;
    setBusy(f.fileId);
    try {
      await fetch(`/api/bookings/${bookingId}/hubspot-files/${f.fileId}`, "DELETE");
      toast({ title: "Detached", description: `${f.name} is no longer on the deal.` });
      load();
    } catch (e: any) {
      toast({ title: "Couldn't detach", description: e?.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  if (!hasDeal) {
    return (
      <Card className="w-full">
        <h3 className="text-lg font-semibold">Documents</h3>
        <p className="mt-2 text-sm text-slate-500">No HubSpot deal is linked to this booking, so there's nowhere to read documents from.</p>
      </Card>
    );
  }

  return (
    <Card className="flex w-full flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Documents</h3>
          <p className="text-xs text-slate-500">Files on the HubSpot deal, read live{state && !state.live ? " — HubSpot unreachable, showing nothing" : ""}</p>
        </div>
        <div>
          <input ref={input} type="file" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <Button variant="outline" disabled={busy === "upload"} onClick={() => input.current?.click()}>
            {busy === "upload" ? "Uploading…" : "Upload to deal"}
          </Button>
        </div>
      </div>

      {state?.error && <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">{state.error}</p>}
      {state && state.live && state.files.length === 0 && <p className="text-sm text-slate-500">Nothing on the deal yet.</p>}

      {state && state.files.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {state.files.map((f) => (
            <li key={f.fileId} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{f.name}</p>
                <p className="text-xs text-slate-500">
                  {[f.extension?.toUpperCase(), fmtSize(f.size), f.createdAt ? new Date(f.createdAt).toLocaleDateString("en-GB") : null, f.fromHelm ? "via HELM app" : "added in HubSpot"]
                    .filter(Boolean)
                    .join(" · ")}
                  {f.note ? ` · “${f.note}”` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <a className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-3 text-xs font-medium hover:bg-slate-50" href={openHref(f)} target="_blank" rel="noreferrer noopener">Open</a>
                <Button size="sm" variant="ghost" disabled={busy === f.fileId} onClick={() => remove(f)}>Remove</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
